import { PaginatedResponse } from '../../models/PaginatedResponse.model'
import { ObstructionTypeUpdateEvent } from '../../events/ObstructionTypeUpdateEvent'
import { ObstructionTypeSearchEvent } from '../../events/ObstructionTypeSearchEvent'

import FlowfeedApiQueryService, {
  FlowfeedApiQueryServiceGetEvent,
} from '../FlowfeedApiQueryService/FlowfeedApiQueryService'
import { ObstructionType } from '../../models/ObstructionType.model'
import { ObstructionTypeDTO } from '../../models/ObstructionTypeDTO.model'
import { ObstructionTypeAddEvent } from '../../events/ObstructionTypeAddEvent'
import { AppRegions } from '../../models/AppRegions.model'

export class ObstructionTypeService {
  /**
   * @description Search for a single obstructionType by id
   *
   * @param id
   * @param relations
   *
   * @returns promise for a Obstruction
   */
  public async get({ id }: { id?: number }): Promise<ObstructionType> {
    const params = {
      id,
    }
    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: `obstruction-type/get`,
      params,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const obstructionTypeRaw = await flowfeedApiQueryService.get<ObstructionTypeDTO>(flowfeedApiQueryRequest)
      return ObstructionTypeService.formatObstruction(obstructionTypeRaw)
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }
  }

  public async search(event: ObstructionTypeSearchEvent): Promise<PaginatedResponse<ObstructionType>> {
    const params = {
      ...event,
      name: event.name || undefined,
      orderDirection: event.orderDirection?.toUpperCase(),
    }

    const flowfeedApiQueryRequest: FlowfeedApiQueryServiceGetEvent = {
      endpoint: 'obstruction-type',
      params,
    }

    const obstructionTypes: {
      data: ObstructionType[]
      total: number
    } = {
      data: [],
      total: 0,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const rawObstructions = await flowfeedApiQueryService.get<PaginatedResponse<ObstructionTypeDTO>>(
        flowfeedApiQueryRequest,
      )
      // wrap to avoid errors if the request is cancelled
      if (rawObstructions?.data) {
        obstructionTypes.data = rawObstructions.data.map((obstructionType) =>
          ObstructionTypeService.formatObstruction(obstructionType),
        )
        obstructionTypes.total = rawObstructions.total
      }
    } catch (error) {
      console.error(error)
      throw 'Sorry, there was a problem with your request.'
    }

    return obstructionTypes
  }

  public async add(obstructionType: ObstructionTypeAddEvent): Promise<ObstructionType> {
    const flowfeedApiQueryRequest = {
      endpoint: 'obstruction-type',
      data: obstructionType,
    }

    const flowfeedApiQueryService = new FlowfeedApiQueryService()
    try {
      const obstructionTypeRaw = await flowfeedApiQueryService.post<ObstructionTypeDTO>(flowfeedApiQueryRequest)
      return ObstructionTypeService.formatObstruction(obstructionTypeRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async update(id: number, obstructionType: ObstructionTypeUpdateEvent): Promise<ObstructionType> {
    const flowfeedApiQueryRequest = {
      endpoint: `obstruction-type/${id}`,
      data: obstructionType,
    }
    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      const obstructionTypeRaw = await flowfeedApiQueryService.patch<ObstructionTypeDTO>(flowfeedApiQueryRequest)
      return ObstructionTypeService.formatObstruction(obstructionTypeRaw)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw error.message
      } else if ((error as { data: { message: string } })?.data?.message) {
        throw (error as { data: { message: string } })?.data?.message
      }
      throw error
    }
  }

  public async delete(id: number): Promise<void> {
    const flowfeedApiQueryRequest = {
      endpoint: `obstruction-type/${id}`,
    }

    try {
      const flowfeedApiQueryService = new FlowfeedApiQueryService()
      await flowfeedApiQueryService.delete(flowfeedApiQueryRequest)
    } catch (error) {
      console.error(error)
      if (error instanceof Error) {
        throw new Error(error.message)
      }
      const errorData = error as { data?: { message?: string } }
      throw errorData?.data?.message ?? error
    }
  }

  public static formatObstruction(obstructionType: ObstructionTypeDTO): ObstructionType {
    return {
      id: obstructionType.id,
      sortWeight: obstructionType.sortWeight,
      name: obstructionType.name,
      symbol: obstructionType.symbol,
      active: obstructionType.active,
      region: {
        code: obstructionType.region.code as AppRegions,
        name: obstructionType.region.name || '',
      },
      createdAt: new Date(obstructionType.createdAt),
    }
  }
}
